/**
 * Tableau de bord (/) — accueil : date, salutation, bandeau d'indicateurs,
 * raccourcis, colonne « À traiter » (alertes) et « Derniers mouvements ».
 */
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileStack, Download, Upload, FileText } from 'lucide-react';
import { dashboardApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatInt, longDateUpper, formatMovementTime, formatSigned } from '../../utils/format';
import { Tag, Skeleton } from '../../components/ui/kit';
import { Hearts } from '../../components/wine/wine';
import { ALERT_LABELS } from '../../constants';

function Kpi({ label, value, sub, testId }) {
  return (
    <div className="flex-1 px-5 py-4" data-testid={testId}>
      <p className="kicker mb-2">{label}</p>
      <p className="text-5xl leading-none nums">{value}</p>
      <p className="text-[13px] text-n600 mt-2">{sub}</p>
    </div>
  );
}

function Shortcut({ icon: Icon, title, subtitle, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className="vd-card px-4 py-4 flex items-center gap-3 text-left hover:bg-surface transition-colors">
      <Icon className="w-5 h-5 text-gold700 shrink-0" strokeWidth={1.6} />
      <span>
        <span className="block text-[14px] font-medium">{title}</span>
        <span className="block text-[12px] text-n500">{subtitle}</span>
      </span>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: stats } = useQuery({ queryKey: ['dashboard-stats'], queryFn: dashboardApi.stats });
  const { data: alerts = [] } = useQuery({ queryKey: ['alerts'], queryFn: dashboardApi.alerts });
  const { data: movements = [] } = useQuery({ queryKey: ['dashboard-movements'], queryFn: dashboardApi.movements });

  return (
    <div className="p-6 md:p-8 max-w-[1200px]">
      <p className="kicker">{longDateUpper()}</p>
      <h1 className="font-title text-5xl mt-1 mb-6">Bienvenue dans Cavorama {(user?.name || '').trim().split(/\s+/)[0]} :)</h1>

      {/* Bandeau d'indicateurs */}
      <div className="vd-card flex flex-col sm:flex-row divide-y sm:divide-y-0 sm:divide-x divide-hair mb-5" data-testid="kpi-strip">
        {stats ? (
          <>
            <Kpi testId="kpi-bottles" label="Bouteilles" value={formatInt(stats.bottles)}
              sub={`${stats.cuvees} cuvées · ${stats.references} références`} />
            <Kpi testId="kpi-aboire" label="À boire maintenant" value={formatInt(stats.a_boire_count)}
              sub={`${formatInt(stats.a_boire_bottles)} bouteilles concernées`} />
            <Kpi testId="kpi-lowstock" label="Stock faible · rupture" value={formatInt(stats.low_stock_count)}
              sub={`dont ${stats.low_stock_hearts} coups de cœur`} />
            <Kpi testId="kpi-sorties" label="Sorties cette semaine" value={formatInt(stats.sorties_week)}
              sub={`dont ${stats.emporter_week} à emporter`} />
          </>
        ) : (
          <div className="p-5 w-full"><Skeleton className="h-20 w-full" /></div>
        )}
      </div>

      {/* Raccourcis */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <Shortcut icon={FileStack} title="Facture · BL" subtitle="Photo → entrée groupée" onClick={() => navigate('/scanner')} testId="shortcut-facture" />
        <Shortcut icon={Download} title="Nouvelle bouteille" subtitle="Étiquette → fiche" onClick={() => navigate('/scanner')} testId="shortcut-nouvelle" />
        <Shortcut icon={Upload} title="Sortie rapide" subtitle="Mode service" onClick={() => navigate('/sortie')} testId="shortcut-sortie" />
        <Shortcut icon={FileText} title="Carte des vins" subtitle="Dernière : 02/09 · 3 changements" onClick={() => navigate('/carte')} testId="shortcut-carte" />
      </div>

      {/* Deux colonnes */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* À traiter */}
        <section data-testid="a-traiter">
          <div className="flex items-baseline justify-between mb-2 hair-b pb-2">
            <h3 className="font-title text-2xl">À traiter</h3>
            <span className="text-[13px] text-n500">{alerts.length} alertes</span>
          </div>
          <div>
            {alerts.map((a, i) => (
              <button
                key={i}
                data-testid={`alert-row-${i}`}
                onClick={() => a.cuvee_id && navigate(`/cave/cuvee/${a.cuvee_id}`)}
                className="w-full flex items-start gap-3 py-3 hair-b text-left hover:bg-surface/60 transition-colors"
              >
                <span className="w-20 shrink-0 pt-0.5">
                  <Tag tone={a.type === 'a_boire' ? 'fill' : 'accent'}>{ALERT_LABELS[a.type]}</Tag>
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="font-title text-lg leading-tight">{a.label}</span>
                    {a.hearts > 0 && <Hearts count={a.hearts} size={12} />}
                  </span>
                  <span className="block text-[12.5px] text-n600">{a.detail}</span>
                </span>
                <span className="shrink-0 text-gold700 text-[13px] pt-0.5">{a.action}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Derniers mouvements */}
        <section data-testid="derniers-mouvements">
          <div className="flex items-baseline justify-between mb-2 hair-b pb-2">
            <h3 className="font-title text-2xl">Derniers mouvements</h3>
            <button className="vd-link text-[13px]" onClick={() => navigate('/cave/mouvements')} data-testid="historique-link">Historique</button>
          </div>
          <div>
            {movements.map((m) => (
              <div key={m.id} className="flex items-start gap-3 py-3 hair-b" data-testid={`movement-row-${m.id}`}>
                <span className="w-12 shrink-0 text-[13px] text-n500 nums pt-0.5">{formatMovementTime(m.date)}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] leading-tight">{m.label}</span>
                  <span className="block text-[12px] text-n500">{m.user_name} · {m.type_label?.toLowerCase()}</span>
                </span>
                <span className={`shrink-0 nums text-[14px] ${m.quantity > 0 ? 'text-gold700' : 'text-ink'}`}>{formatSigned(m.quantity)}</span>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
